/**
 * 🧮 인포트렌드 사이징 툴 (v2.233.0 · 2026-10-08)
 *
 * 사용자 지시: 「포털에 인포트렌드 사이징 툴이라고 분석쪽에다 새로운 메뉴를 하나 만들어줘. 첨부한 엑셀파일은 인포트렌드
 *   사이징 툴인데, 엑셀문서 잘 분석해서 스토리지 Raid용량계산, 카메라 별 스토리지 용량계산등 엑셀에 있는 기능들 정리해서
 *   한글로 보기쉽게 표현해 주고, 계산해주는 툴을 만들어줘」
 *
 * 원본: 회사 NAS 「2. 영업부서/01. 업체별 영업진행 (견적서)/Copy of Sizing Tool_v2.1.xlsx」 (Infortrend · 2016-03 · v2.1).
 *   시트 14개(보이는 6 · 숨은 8)를 뜯어 다섯 계산기와 그 뒤의 표를 옮겼다. 계산은 lib/sizing/calc.ts(순수 함수) ·
 *   표는 lib/sizing/data.ts · 디스크 성능 219줄은 lib/sizing/disk-perf-table.ts(스크립트로 뽑음).
 *   그물 scripts/check-sizing.ts 가 엑셀이 셀에 남긴 결과값과 맞춰 본다.
 *
 * 서버가 없다 — 전부 화면 안에서 계산한다(저장 안 함). 권한: 전 직원(원가가 아니라 공개 정가라 viewer 도 본다).
 * 탭은 ?tab= 으로 — 특정 계산기 주소를 그대로 보낼 수 있게.
 *
 * v2.233.3 (10-09 · 사용자 「PC 에서 쓰기에 글씨가 작고 여백이 많고 가운데로 쏠려 있다」): 가운데 1,152px 상자(max-w-6xl mx-auto)를
 *   버리고 다른 화면처럼 **본문 폭을 다 쓴다** · 제목·탭·설명 글자를 한 단계씩 키웠다. 상자 안 글자·결과 타일은 SizingUi 쪽.
 *
 * v2.313.2 (10-30 · 사용자 「용량계산 페이지를 첫 페이지로 · 안내는 제일 오른쪽 끝으로 · 안내 글씨 1포인트 줄여 ·
 *   디스크 1개 용량 기본 24TB(24TB 기준 계산을 제일 많이 쓴다)」): 메뉴를 열면(?tab= 없음) 💽 용량 계산 ·
 *   📖 안내 탭은 맨 끝 · 안내 탭 글(표·목록) 14px → 13px · 24TB 기본값은 CapacityCalc 쪽.
 */
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CapacityCalc } from '../components/sizing/CapacityCalc';
import { PerfCalc } from '../components/sizing/PerfCalc';
import { MediaCalc } from '../components/sizing/MediaCalc';
import { CctvCalc } from '../components/sizing/CctvCalc';
import { VdiCalc } from '../components/sizing/VdiCalc';
import { Panel, SimpleTable } from '../components/sizing/SizingUi';
import { PageTabs } from '../components/ui/PageTabs';

type Tab = 'guide' | 'capacity' | 'perf' | 'media' | 'cctv' | 'vdi';
// 계산기 다섯 → 안내는 맨 끝 (v2.313.2 · 제일 많이 쓰는 용량 계산이 첫 탭이자 메뉴를 열 때 보이는 화면)
const TABS: { key: Tab; label: string }[] = [
  { key: 'capacity', label: '💽 용량 계산' },
  { key: 'perf', label: '⚡ 성능 계산' },
  { key: 'media', label: '🎬 영상 제작' },
  { key: 'cctv', label: '📹 CCTV' },
  { key: 'vdi', label: '🖥 VDI' },
  { key: 'guide', label: '📖 안내' },
];
const isTab = (v: string | null): v is Tab => TABS.some((t) => t.key === v);

export function SizingToolPage() {
  const [sp, setSp] = useSearchParams();
  const raw = sp.get('tab');
  const tab: Tab = isTab(raw) ? raw : 'capacity';
  const setTab = (t: Tab) => { const next = new URLSearchParams(sp); next.set('tab', t); setSp(next, { replace: true }); };
  // [↺ 처음값으로] — 계산기를 통째로 다시 그려 처음 값(엑셀 기본값 · 용량 계산의 디스크 크기만 24TB)으로 돌린다 (값을 이것저것 바꾸다 길을 잃으면 쓰는 단추 · 10-08 검토)
  const [resetKey, setResetKey] = useState(0);

  return (
    <div className="p-4 md:p-6">
      <div className="flex items-end justify-between flex-wrap gap-x-4 gap-y-1 mb-1">
        <h1 className="ui-page-title">인포트렌드 사이징 툴</h1>
        <span className="ui-help">원본: Infortrend Sizing Tool v2.1 (2016년 3월) · 결과는 참고용</span>
      </div>
      <p className="ui-page-sub mb-4 leading-relaxed">
        스토리지 제안 때 쓰던 인포트렌드 엑셀 사이징 툴을 포털로 옮긴 것입니다.{' '}
        <span className="font-semibold text-emerald-700">초록 상자</span>에 넣으면 <span className="font-semibold text-amber-700">노란 상자</span>에서 바로 계산됩니다(저장되지 않음).
        모델 이름과 정가(MSRP · USD)는 2015~2017년 당시 기준이라 참고로만 보세요.
      </p>

      <PageTabs
        tabs={TABS}
        value={tab}
        onChange={setTab}
        ariaLabel="사이징 툴 보기"
        className="mb-5"
        right={tab !== 'guide' ? (
          <button type="button" onClick={() => setResetKey((k) => k + 1)} title="이 계산기의 입력을 처음 값으로 되돌립니다"
            className="btn btn-secondary">
            ↺ 처음값으로
          </button>
        ) : null}
      />

      {tab === 'guide' && <GuideTab onGo={setTab} />}
      {tab === 'capacity' && <CapacityCalc key={resetKey} />}
      {tab === 'perf' && <PerfCalc key={resetKey} />}
      {tab === 'media' && <MediaCalc key={resetKey} />}
      {tab === 'cctv' && <CctvCalc key={resetKey} />}
      {tab === 'vdi' && <VdiCalc key={resetKey} />}
    </div>
  );
}

function GuideTab({ onGo }: { onGo: (t: Tab) => void }) {
  const go = (t: Tab, label: string) => (
    <button type="button" onClick={() => onGo(t)} className="text-blue-700 hover:underline font-semibold">{label}</button>
  );
  // 안내 글은 계산기 화면보다 1포인트 작게 — 표·목록 14px → 13px (v2.313.2 · 사용자 「안내부분 글씨가 너무 커」 · 폰 표는 원래 12px 그대로)
  return (
    <div className="space-y-4">
      <Panel title="계산기 다섯 가지 — 엑셀의 시트 하나가 탭 하나">
        <SimpleTable
          wrap
          textClass="text-xs md:text-[13px]"
          head={['탭', '무엇을 넣나', '무엇이 나오나', '언제 쓰나']}
          rows={[
            [go('capacity', '💽 용량 계산'), '필요한 용량 · 디스크 크기 · 스페어 · 논리 드라이브 수 (또는 디스크 수)', 'RAID 1·5·6 마다 필요한 디스크 수와 인클로저 수 · 실제 쓸 수 있는 용량', '일반 데이터·아카이브·백업처럼 용량만 맞추면 되는 제안'],
            [go('perf', '⚡ 성능 계산'), '랜덤/순차 · 읽기/쓰기/OLTP · RAID · 디스크 종류 · 블록 크기 · 목표 IOPS 또는 MB/s · 폼팩터', '디스크 1개 성능 · 필요한 디스크 수 · RAID/JBOD 권장 수 (반대로 디스크 수 → 성능)', 'DB·가상화처럼 IOPS 나 전송량 목표가 있는 제안 (인포트렌드 IOMeter 실측 표)'],
            [go('media', '🎬 영상 제작'), '재생·입력 스트림의 프레임 크기 · fps · 압축 · 스트림 수 + 용량 · 디스크 크기 · RAID', '처리량(MB/s) · 제품군별 필요한 디스크 · DS 3000/4000 + JBOD 추천 표 (정가 포함)', '방송·편집(M&E) 스토리지 제안'],
            [go('cctv', '📹 CCTV'), '카메라 종류 3가지 — 형식 · 해상도 · 화질 · fps · 대수 · 하루 녹화 시간 · 보관 일수', '대역폭 · 보관 용량(TB) · 단일/이중 컨트롤러 · 디스크 크기별 DS 1012/1016 + JBOD 추천 표', '관제센터·CCTV 녹화 스토리지 제안'],
            [go('vdi', '🖥 VDI'), '가상 데스크톱 수 · IOPS · 용량 · RAM · 클론 · RAID · 디스크 (또는 디스크 구성)', '권장 모델 · 디스크 수 · LUN 구성 · 부트 스톰 대책 (반대로 구성 → 지원 데스크톱 수)', '가상 데스크톱(VDI) 스토리지 제안'],
          ]}
        />
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="공통으로 알아 둘 것">
          <ul className="text-[13px] text-slate-700 space-y-2 list-disc pl-5 leading-relaxed">
            <li><b>디스크 용량 표기와 실제</b> — 제조사 4TB 는 4 × 1,000⁴ 바이트 = 3,814,697MB (= 3.64TiB). 모든 계산은 실제 MB 로 한다.</li>
            <li><b>디스크마다 256MB 예약, 논리 드라이브마다 256MB 메타데이터, 가상화(볼륨 관리) 몫 0.2%</b> 를 뺀다. 그래서 4TB 디스크 1개로 실제 쓸 수 있는 건 약 3.63TB.</li>
            <li><b>패리티</b> — RAID 5 는 묶음마다 1개, RAID 6 은 2개가 데이터가 아니다. RAID 1 은 절반.</li>
            <li><b>스페어</b> — 쓰지 않고 대기하는 디스크. 영상·CCTV 계산기는 인클로저(서랍)마다 0 또는 1개.</li>
            <li><b>인클로저·JBOD</b> — 12·16·24베이는 한 통이 한 단위, 48·60베이는 서랍(16개·20개) 3개가 한 통. JBOD 는 체인으로 잇는데 한도가 있다(12·16·24베이 14대 · 48베이 6대 · 60베이 7대).</li>
            <li><b>「권장」 디스크 수</b> — 논리 드라이브를 만들기 좋게 인클로저나 서랍을 가득 채운 수. 「정확히 필요한 수」보다 많다.</li>
            <li><b>단위</b> — IOPS 는 초당 입출력 횟수(작은 랜덤 I/O), MB/s 는 초당 전송량(큰 순차 I/O). 어느 쪽이 중요한지에 따라 디스크 수가 크게 달라진다.</li>
          </ul>
        </Panel>
        <Panel title="주의 · 엑셀 원본과 다른 점">
          <ul className="text-[13px] text-slate-700 space-y-2 list-disc pl-5 leading-relaxed">
            <li><b>참고용</b>입니다 — 엑셀 원본의 안내 그대로 「실제 운영·애플리케이션에 따라 결과가 달라질 수 있으니 인포트렌드 프리세일즈에 확인」.</li>
            <li><b>모델·정가는 2015~2017년</b> — EonStor DS 1000/3000/4000 Gen2 · JB 2000/3000 과 당시 USD 정가. 계산 규칙은 지금도 쓸 수 있지만 제품군은 바뀌었다. 새 사이징 툴을 받으면 표만 바꾸면 된다.</li>
            <li>엑셀이 <b>0 으로 나누던 자리</b>(처리량 0 · 프레임 「없음」)는 오류 대신 0 으로 본다.</li>
            <li>VDI 모델 판정의 한 가지(47,000~75,000 IOPS · 361~444개)는 엑셀 수식에 빈 인자가 끼어 늘 빈칸이었다 — 옆 가지와 같은 뜻(DS 4000U)으로 채웠다.</li>
            <li>CCTV 컨트롤러 판정의 경계값(정확히 352 MB/s)은 엑셀에서 어느 쪽도 아니었다 — 단일 컨트롤러 쪽으로 넣었다.</li>
            <li>CCTV 표는 엑셀이 4·6·8TB 만 보여 줬는데 숨은 표에 있던 1·2·3·5TB 도 고를 수 있게 했다. 호스트 보드 선택도 숨은 시트에서 꺼냈다(가격에만 영향).</li>
            <li>원본 파일: NAS <span className="font-mono text-xs">2. 영업부서 › 01. 업체별 영업진행 (견적서) › Copy of Sizing Tool_v2.1.xlsx</span></li>
          </ul>
        </Panel>
      </div>
    </div>
  );
}
